import { useEffect, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { PageShell } from '@/components/primitives/PageShell';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { getPublicTenants } from '@/api/public';

export default function OnboardingGate() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [hasTenants, setHasTenants] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = (await getPublicTenants()) as unknown[] | undefined;
        const tenants = Array.isArray(data) ? data : [];
        if (!cancelled) setHasTenants(tenants.length > 0);
      } catch (err) {
        // Fail open — better to surface the dashboard than block forever.
        console.error('OnboardingGate: tenants probe failed, failing open', err);
        if (!cancelled) setHasTenants(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <PageShell>
        <Skeleton className="mb-3 h-8 w-1/3" />
        <Skeleton className="mb-2 h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </PageShell>
    );
  }

  if (hasTenants) return <Outlet />;

  // Belt-and-braces: /onboarding/* lives outside this gate but in case of a
  // race we render normally rather than loop.
  if (location.pathname.startsWith('/onboarding/')) return <Outlet />;

  const canCreate = isAdmin() || isSuperAdmin();
  if (canCreate) return <Navigate to="/onboarding/create-tenant" replace />;

  return (
    <PageShell>
      <div className="mx-auto max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>System not yet configured</CardTitle>
            <CardDescription>
              No tenants are configured yet for this deployment.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertDescription>
                Please contact a system administrator to complete setup before
                you can access the dashboard.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    </PageShell>
  );
}
